/**
 * FILE: src/blueprint/components/insights/CoachStream.jsx
 *
 * GOAL:
 * - Render coach entries as one sequential stream.
 * - Keep typing/cache logic inside CoachStream.
 * - Keep interaction-flow state outside CoachStream.
 */

import { useEffect, useMemo, useRef, useState } from '@wordpress/element';
import { formatCoachEntry } from '../../utils/formatCoachEntry.js';
import CoachHeader from './CoachHeader.jsx';
import CoachMessage from './CoachMessage.jsx';

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function asString(value) {
  return typeof value === 'string' ? value : '';
}

function getIconSymbol(name) {
  const key = asString(name).trim().toLowerCase();

  if (key === 'fish') return '🐟';
  if (key === 'tomato') return '🍅';
  if (key === 'tree') return '🌳';

  return '◦';
}

function getTypedSessionCacheKey(sessionKey, itemId) {
  return `digivibe_coach_typed__${sessionKey}::${itemId}__${sessionKey}`;
}

function hasTypedSessionCache(sessionKey, itemId) {
  if (typeof window === 'undefined') return false;

  const safeSessionKey = asString(sessionKey).trim();
  const safeItemId = asString(itemId).trim();

  if (!safeSessionKey || !safeItemId) return false;

  try {
    return (
      window.sessionStorage.getItem(getTypedSessionCacheKey(safeSessionKey, safeItemId)) === '1'
    );
  } catch {
    return false;
  }
}

function normalizeCoachStreamItems(entries, debug = false) {
  if (!Array.isArray(entries)) {
    if (debug) {
      console.warn('[CoachStream] entries is not an array', entries);
    }
    return [];
  }

  const out = [];

  for (const entry of entries) {
    if (!entry || typeof entry !== 'object') {
      if (debug) console.warn('[CoachStream] skipped entry: invalid object', entry);
      continue;
    }

    if (entry.role !== 'coach') {
      if (debug) {
        console.warn('[CoachStream] skipped entry: role is not coach', {
          id: entry?.id,
          role: entry?.role,
          entry,
        });
      }
      continue;
    }

    const entryId = asString(entry.id).trim();
    if (!entryId) {
      if (debug) console.warn('[CoachStream] skipped entry: missing entry.id', entry);
      continue;
    }

    const title = asString(entry.title);
    const blocks = asArray(entry.blocks);

    for (let i = 0; i < blocks.length; i += 1) {
      const block = blocks[i];

      if (!block || typeof block !== 'object') {
        continue;
      }

      const type = asString(block.type).trim() || 'text';
      const id = asString(block.id).trim() || `${entryId}.${i + 1}`;

      if (type === 'choices') {
        const choices = asArray(block.choices)
          .map((choice) => {
            if (!choice || typeof choice !== 'object') return null;

            const choiceId = asString(choice.id).trim();
            const label = asString(choice.label).trim();

            if (!choiceId || !label) return null;

            return {
              ...choice,
              id: choiceId,
              label,
            };
          })
          .filter(Boolean);

        if (!choices.length) continue;

        out.push({
          id,
          type: 'choices',
          entryId,
          blockIdx: i,
          title,
          layout: asString(block.layout).trim() || 'stack',
          choices,
          rawEntry: entry,
          rawBlock: block,
        });

        continue;
      }

      if (type === 'icon') {
        const name = asString(block.name).trim();
        const label = asString(block.label).trim();

        if (!name) continue;

        out.push({
          id,
          type: 'icon',
          entryId,
          blockIdx: i,
          title,
          name,
          label,
          rawEntry: entry,
          rawBlock: block,
        });

        continue;
      }

      const format = asString(block.format).trim() || 'plain';
      const body = typeof block.body === 'string' ? block.body : '';

      const text =
        format === 'html'
          ? body
          : formatCoachEntry({
              title: i === 0 ? title : '',
              body,
            });

      if (!text) {
        if (debug) {
          console.warn('[CoachStream] skipped block: empty formatted text', {
            id,
            entryId,
            index: i,
            title,
            body: block?.body,
            block,
          });
        }
        continue;
      }

      out.push({
        id,
        type: 'text',
        text,
        format,
        title: i === 0 ? title : '',
        entryId,
        blockIdx: i,
        rawEntry: entry,
        rawBlock: block,
      });
    }
  }

  return out;
}

function resolvePersona(entries) {
  if (!Array.isArray(entries)) {
    return { name: '', slogan: '', avatarSrc: '' };
  }

  for (const entry of entries) {
    const persona = entry?.persona;
    if (!persona || typeof persona !== 'object') {
      continue;
    }

    const name = asString(persona.name).trim();
    const slogan = asString(persona.slogan).trim();
    const avatarSrc = asString(persona?.avatar?.src).trim();

    return { name, slogan, avatarSrc };
  }

  return { name: '', slogan: '', avatarSrc: '' };
}

function CoachChoices({ item, onChoiceSelect = null, disabled = false }) {
  return (
    <div className="digivibe-coach-message digivibe-coach-message--choices">
      {item.title ? <div className="digivibe-coach-message__title">{item.title}</div> : null}

      <div className="digivibe-coach-choices" data-layout={item.layout || 'stack'}>
        {asArray(item.choices).map((choice) => (
          <button
            key={choice.id}
            type="button"
            className="digivibe-coach-choice"
            disabled={disabled || Boolean(choice.disabled)}
            onClick={() => {
              if (typeof onChoiceSelect === 'function') {
                onChoiceSelect({
                  choice,
                  choiceId: choice.id,
                  entryId: item.entryId,
                  blockId: item.id,
                  rawEntry: item.rawEntry,
                  rawBlock: item.rawBlock,
                });
              }
            }}
          >
            {choice.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function CoachIcon({ item }) {
  const label = item.label || item.name;

  return (
    <div className="digivibe-coach-message digivibe-coach-message--icon">
      {item.title ? <div className="digivibe-coach-message__title">{item.title}</div> : null}

      <div className="digivibe-coach-icon" aria-label={label}>
        <span className="digivibe-coach-icon__symbol" aria-hidden="true">
          {getIconSymbol(item.name)}
        </span>
        {label ? <span className="digivibe-coach-icon__label">{label}</span> : null}
      </div>
    </div>
  );
}

function entryHasChoices(entry) {
  return asArray(entry?.blocks).some((block) => asString(block?.type).trim() === 'choices');
}

function isLastEntryBlock(item) {
  const blocks = asArray(item?.rawEntry?.blocks);

  if (!blocks.length) {
    return true;
  }

  return Number(item?.blockIdx) >= blocks.length - 1;
}

function CoachInteractionControls({
  item,
  closedFeedbackIds = [],
  onCoachFeedback = null,
  onRestartChoices = null,
}) {
  const entryId = asString(item?.entryId).trim();

  if (!entryId) {
    return null;
  }

  const isInteraction = asString(item?.rawEntry?.delivery).trim() === 'interaction';

  if (!isInteraction || !isLastEntryBlock(item)) {
    return null;
  }

  const isFeedbackClosed = asArray(closedFeedbackIds).includes(entryId);
  const isDrillNode = entryHasChoices(item.rawEntry);

  return (
    <div className="digivibe-coach-interaction-controls" data-entry-id={entryId}>
      {isDrillNode || isFeedbackClosed ? null : (
        <>
          <button
            type="button"
            className="digivibe-coach-feedback-button"
            aria-label="Dit helpt"
            onClick={() => {
              if (typeof onCoachFeedback === 'function') {
                onCoachFeedback({
                  entryId,
                  value: 'helpful',
                  item,
                });
              }
            }}
          >
            👍
          </button>

          <button
            type="button"
            className="digivibe-coach-feedback-button"
            aria-label="Dit helpt niet"
            onClick={() => {
              if (typeof onCoachFeedback === 'function') {
                onCoachFeedback({
                  entryId,
                  value: 'not_helpful',
                  item,
                });
              }
            }}
          >
            👎
          </button>
        </>
      )}

      <button
        type="button"
        className="digivibe-coach-restart-button"
        onClick={() => {
          if (typeof onRestartChoices === 'function') {
            onRestartChoices({
              entryId,
              item,
            });
          }
        }}
      >
        Terug naar alle opties
      </button>
    </div>
  );
}

export default function CoachStream({
  entries = [],
  name = '',
  slogan = '',
  avatarSrc = '',
  avatarText = 'C',
  sessionKey = '',
  autoScroll = true,
  speed = {},
  instant = false,
  className = '',
  debug = false,
  onChoiceSelect = null,
  onCoachFeedback = null,
  onRestartChoices = null,
  closedFeedbackIds = [],
  hiddenChoiceBlockIds = [],
  isInteractionLoading = false,
}) {
  const items = useMemo(() => normalizeCoachStreamItems(entries, debug), [entries, debug]);
  const resolvedPersona = useMemo(() => resolvePersona(entries), [entries]);

  const coachName = name || resolvedPersona.name || 'Coach';
  const coachSlogan = slogan || resolvedPersona.slogan || '';
  const coachAvatarSrc = avatarSrc || resolvedPersona.avatarSrc || '';

  const [typedIds, setTypedIds] = useState(() => new Set());
  const [activeId, setActiveId] = useState(null);
  const [queue, setQueue] = useState([]);

  const doneIdsRef = useRef(new Set());
  const activeIdRef = useRef(activeId);
  const typedIdsRef = useRef(typedIds);
  const lockedTextByIdRef = useRef(new Map());

  activeIdRef.current = activeId;
  typedIdsRef.current = typedIds;

  const cachedTypedIds = useMemo(() => {
    const next = new Set();

    for (const item of items) {
      if (item.type === 'text' && hasTypedSessionCache(sessionKey, item.id)) {
        next.add(item.id);
      }
    }

    return next;
  }, [items, sessionKey]);

  useEffect(() => {
    if (debug) {
      console.groupCollapsed('[CoachStream] stream changed');
      console.log({ sessionKey, instant });
      console.groupEnd();
    }

    lockedTextByIdRef.current = new Map();
    doneIdsRef.current = new Set();
    setActiveId(null);
    setQueue([]);
  }, [sessionKey, instant, debug]);

  useEffect(() => {
    if (instant) return;

    const validIds = new Set(items.map((item) => item.id));

    setQueue((prevQueue) => prevQueue.filter((id) => validIds.has(id)));

    setTypedIds((prevTypedIds) => {
      const next = new Set();

      for (const id of prevTypedIds) {
        if (validIds.has(id)) {
          next.add(id);
        }
      }

      return next;
    });

    setActiveId((prevActiveId) => {
      if (!prevActiveId) return null;
      return validIds.has(prevActiveId) ? prevActiveId : null;
    });
  }, [items, instant]);

  useEffect(() => {
    for (const item of items) {
      if (item.type === 'text' && !lockedTextByIdRef.current.has(item.id)) {
        lockedTextByIdRef.current.set(item.id, item.text);
      }
    }

    if (instant) {
      return;
    }

    setQueue((prevQueue) => {
      const knownIds = new Set();

      for (const id of prevQueue) knownIds.add(id);
      for (const id of typedIdsRef.current) knownIds.add(id);
      for (const id of cachedTypedIds) knownIds.add(id);
      if (activeIdRef.current) knownIds.add(activeIdRef.current);

      const newIds = items.map((item) => item.id).filter((id) => id && !knownIds.has(id));

      return newIds.length ? [...prevQueue, ...newIds] : prevQueue;
    });
  }, [items, instant, cachedTypedIds]);

  useEffect(() => {
    if (instant) return;
    if (activeId) return;
    if (!queue.length) return;

    setActiveId(queue[0]);
  }, [queue, activeId, instant]);

  const handleDone = (id) => {
    if (!id || instant) return;
    if (doneIdsRef.current.has(id)) return;

    doneIdsRef.current.add(id);

    setTypedIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });

    setQueue((prev) => {
      const nextQueue = prev.filter((queuedId) => queuedId !== id);
      setActiveId(nextQueue.length ? nextQueue[0] : null);
      return nextQueue;
    });
  };

  useEffect(() => {
    if (instant) return;
    if (!activeId) return;

    const activeItem = items.find((item) => item.id === activeId);

    if (!activeItem) return;

    if (activeItem.type !== 'text') {
      window.setTimeout(() => {
        handleDone(activeItem.id);
      }, 0);
    }
  }, [activeId, items, instant]);

  const visibleItems = useMemo(() => {
    const withLockedText = (item) => ({
      ...item,
      text:
        item.type === 'text'
          ? lockedTextByIdRef.current.get(item.id) || item.text
          : item.text || '',
    });

    if (instant) {
      return items.map(withLockedText);
    }

    return items
      .filter(
        (item) => typedIds.has(item.id) || cachedTypedIds.has(item.id) || activeId === item.id
      )
      .map(withLockedText);
  }, [items, typedIds, cachedTypedIds, activeId, instant]);

  const hiddenChoiceIds = useMemo(() => {
    return new Set(
      asArray(hiddenChoiceBlockIds)
        .map((id) => asString(id).trim())
        .filter(Boolean)
    );
  }, [hiddenChoiceBlockIds]);

  const rootClassName = ['digivibe-coach-stream', className].filter(Boolean).join(' ');

  return (
    <div className={rootClassName}>
      <CoachHeader
        name={coachName}
        slogan={coachSlogan}
        avatarSrc={coachAvatarSrc}
        avatarText={avatarText}
      />

      <div className="digivibe-coach-stream__messages">
        {visibleItems.map((item) => {
          if (item.type === 'choices') {
            if (hiddenChoiceIds.has(item.id)) {
              return null;
            }

            return (
              <div key={item.id}>
                <CoachChoices
                  item={item}
                  onChoiceSelect={onChoiceSelect}
                  disabled={isInteractionLoading}
                />
                <CoachInteractionControls
                  item={item}
                  closedFeedbackIds={closedFeedbackIds}
                  onCoachFeedback={onCoachFeedback}
                  onRestartChoices={onRestartChoices}
                />
              </div>
            );
          }

          if (item.type === 'icon') {
            return (
              <div key={item.id}>
                <CoachIcon item={item} />
                <CoachInteractionControls
                  item={item}
                  closedFeedbackIds={closedFeedbackIds}
                  onCoachFeedback={onCoachFeedback}
                  onRestartChoices={onRestartChoices}
                />
              </div>
            );
          }

          if (instant) {
            return (
              <div key={item.id} className="digivibe-coach-stream__instantMessage">
                <div
                  className="digivibe-coach-message__text"
                  dangerouslySetInnerHTML={
                    item.format === 'html'
                      ? {
                          __html: item.text,
                        }
                      : undefined
                  }
                >
                  {item.format === 'html' ? null : item.text}
                </div>
              </div>
            );
          }

          const isTyped = typedIds.has(item.id) || cachedTypedIds.has(item.id);
          const isActive = activeId === item.id;

          return (
            <div key={item.id}>
              <CoachMessage
                text={item.text}
                format={item.format || 'plain'}
                cacheKey={`${sessionKey}::${item.id}`}
                sessionKey={sessionKey}
                typing={true}
                isActive={isActive}
                autoScroll={autoScroll}
                speed={speed}
                onDone={isTyped ? null : () => handleDone(item.id)}
              />
              <CoachInteractionControls
                item={item}
                closedFeedbackIds={closedFeedbackIds}
                onCoachFeedback={onCoachFeedback}
                onRestartChoices={onRestartChoices}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
